import axios from 'axios';
import { getPartnerToken, readTabContext, writeTabContext, clearTabContext } from '@/services/auth/partnerSessionStorage';

import {
  getRestaurantBackupSnapshot,
  saveRestaurantBackupSnapshot,
} from '@/services/offline/restaurantBackupService';
import { getActiveRestaurantContext } from '@/services/offline/restaurantOfflineData';
import {
  isBackendUnavailableError,
  markBackendAvailable,
  markBackendUnavailable,
  shouldUseOfflineMode,
} from '@/services/connectivity/connectivityService';

export const PRINT_TEMPLATE_BACKUP_MODULE_ID = 'print_templates';
export const PRINT_TEMPLATE_DEFAULT_CHANNEL = 'shared';
export const PRINT_TEMPLATE_DEFAULT_FORMAT = 'html';
export const PRINT_TEMPLATE_DEFAULT_RENDER_ENGINE = 'mustache_v1';

const ACTIVE_RESTAURANT_STORAGE_KEY = 'ewu_tpv_active_restaurant_id';
const LAST_PRINT_TEMPLATE_RESTAURANT_KEY = 'ewu_print_template_last_restaurant_id';
const PRINT_TEMPLATE_BACKUP_LABEL = 'Plantillas de impresion';
const PRINT_TEMPLATE_REQUEST_TIMEOUT_MS = 8000;
// Último token al que el servidor negó la lista de plantillas del partner (401/403).
let partnerTemplatesDeniedToken = null;

function getApiBaseUrl() {
  return process.env.VUE_APP_API_URL || '';
}

function normalizeId(value) {
  if (value === undefined || value === null || value === '') return null;
  return String(value);
}

function normalizeText(value, fallback = '') {
  const normalized = String(value || '').trim();
  return normalized || fallback;
}

function toObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function readStoredValue(key) {
  if (typeof window === 'undefined') return null;

  const readers = [
    () => readTabContext(key),
    () => sessionStorage.getItem(key),
  ];

  for (const read of readers) {
    try {
      const value = read();
      if (value && value !== 'null' && value !== 'undefined') {
        return value;
      }
    } catch {
      // Ignore blocked storage.
    }
  }

  return null;
}

function writeStoredValue(key, value) {
  if (typeof window === 'undefined') return;
  try {
    if (value === undefined || value === null || value === '') {
      clearTabContext(key);
      return;
    }
    writeTabContext(key, String(value));
  } catch (error) {
    console.warn(`No se pudo guardar ${key} en almacenamiento local:`, error);
  }
}

function readPartnerToken() {
  return getPartnerToken();
}

function readAnyAuthToken() {
  return readPartnerToken() || readStoredValue('token');
}

function buildAuthHeaders() {
  const token = readAnyAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function rememberRestaurantId(restaurantId) {
  const normalized = normalizeId(restaurantId);
  if (!normalized) return;
  writeStoredValue(LAST_PRINT_TEMPLATE_RESTAURANT_KEY, normalized);
}

function readRememberedRestaurantId() {
  return normalizeId(
    readStoredValue(LAST_PRINT_TEMPLATE_RESTAURANT_KEY)
    || readStoredValue(ACTIVE_RESTAURANT_STORAGE_KEY)
  );
}

export function normalizePrintTemplate(template = {}) {
  return {
    id: normalizeId(template.id),
    restaurant_id: normalizeId(template.restaurant_id),
    template_key: normalizeText(template.template_key),
    label: normalizeText(template.label),
    channel: normalizeText(template.channel, PRINT_TEMPLATE_DEFAULT_CHANNEL),
    template_format: normalizeText(template.template_format, PRINT_TEMPLATE_DEFAULT_FORMAT),
    render_engine: normalizeText(template.render_engine, PRINT_TEMPLATE_DEFAULT_RENDER_ENGINE),
    paper_width_mm: Number(template.paper_width_mm || 80),
    page_height_mm: Number(template.page_height_mm || 297),
    html_template: String(template.html_template || ''),
    css_template: String(template.css_template || ''),
    data_schema_json: toObject(template.data_schema_json),
    sample_payload_json: toObject(template.sample_payload_json),
    metadata_json: toObject(template.metadata_json),
    version: Number(template.version || 1),
    status: normalizeText(template.status, 'draft'),
    is_active: template.is_active !== false,
    checksum: normalizeText(template.checksum),
    published_at: template.published_at || null,
    created_at: template.created_at || null,
    updated_at: template.updated_at || null,
  };
}

function normalizePrintTemplateManifestEntry(template = {}) {
  const normalized = normalizePrintTemplate(template);
  return {
    id: normalized.id,
    restaurant_id: normalized.restaurant_id,
    template_key: normalized.template_key,
    channel: normalized.channel,
    template_format: normalized.template_format,
    version: normalized.version,
    status: normalized.status,
    is_active: normalized.is_active,
    checksum: normalized.checksum,
    updated_at: normalized.updated_at,
    published_at: normalized.published_at,
  };
}

export function normalizePrintTemplateCollection(payload = {}) {
  const rawTemplates = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.templates)
      ? payload.templates
      : payload?.template_key
        ? [payload]
        : [];

  const seenScopeVersion = new Set();
  const templates = rawTemplates
    .map((template) => normalizePrintTemplate(template))
    .filter((template) => template.template_key && template.template_format)
    .filter((template) => {
      const dedupeKey = [
        template.id || '',
        template.restaurant_id || '',
        template.template_key,
        template.channel,
        template.template_format,
        template.version,
      ].join(':');
      if (seenScopeVersion.has(dedupeKey)) return false;
      seenScopeVersion.add(dedupeKey);
      return true;
    })
    .sort((left, right) => {
      if (left.template_key !== right.template_key) {
        return left.template_key.localeCompare(right.template_key);
      }
      if (left.channel !== right.channel) {
        return left.channel.localeCompare(right.channel);
      }
      return right.version - left.version;
    });

  return {
    templates,
    manifest: templates.map((template) => normalizePrintTemplateManifestEntry(template)),
  };
}

export function selectPublishedPrintTemplate(templates = [], {
  templateKey,
  channel = PRINT_TEMPLATE_DEFAULT_CHANNEL,
  templateFormat = PRINT_TEMPLATE_DEFAULT_FORMAT,
} = {}) {
  const normalizedKey = normalizeText(templateKey);
  const normalizedChannel = normalizeText(channel, PRINT_TEMPLATE_DEFAULT_CHANNEL);
  const normalizedFormat = normalizeText(templateFormat, PRINT_TEMPLATE_DEFAULT_FORMAT);
  if (!normalizedKey) return null;

  const normalizedTemplates = normalizePrintTemplateCollection({ templates }).templates;
  const publishedTemplates = normalizedTemplates.filter((template) => (
    template.template_key === normalizedKey
    && template.template_format === normalizedFormat
    && template.status === 'published'
    && template.is_active
  ));

  const exactChannel = publishedTemplates.filter((template) => template.channel === normalizedChannel);
  if (exactChannel.length > 0) {
    return exactChannel.sort((left, right) => right.version - left.version)[0] || null;
  }

  if (normalizedChannel !== PRINT_TEMPLATE_DEFAULT_CHANNEL) {
    const sharedChannel = publishedTemplates.filter((template) => template.channel === PRINT_TEMPLATE_DEFAULT_CHANNEL);
    if (sharedChannel.length > 0) {
      return sharedChannel.sort((left, right) => right.version - left.version)[0] || null;
    }
  }

  return publishedTemplates.sort((left, right) => right.version - left.version)[0] || null;
}

async function resolveRestaurantId(options = {}) {
  const explicitRestaurantId = normalizeId(options.restaurantId || options.restaurant_id);
  if (explicitRestaurantId) {
    return explicitRestaurantId;
  }

  try {
    const context = await getActiveRestaurantContext();
    if (context?.restaurant_id) {
      return normalizeId(context.restaurant_id);
    }
  } catch {
    // Fallback to remembered id below.
  }

  return readRememberedRestaurantId();
}

export async function getCachedRestaurantPrintTemplates(restaurantId) {
  const normalizedRestaurantId = normalizeId(restaurantId);
  if (!normalizedRestaurantId) {
    return normalizePrintTemplateCollection();
  }

  const snapshot = await getRestaurantBackupSnapshot(normalizedRestaurantId, PRINT_TEMPLATE_BACKUP_MODULE_ID);
  return normalizePrintTemplateCollection(snapshot?.data || {});
}

export async function saveCachedRestaurantPrintTemplates(restaurantId, templates, options = {}) {
  const normalizedRestaurantId = normalizeId(restaurantId);
  if (!normalizedRestaurantId) return null;

  const normalizedCollection = normalizePrintTemplateCollection({ templates });
  rememberRestaurantId(normalizedRestaurantId);
  return saveRestaurantBackupSnapshot(
    normalizedRestaurantId,
    PRINT_TEMPLATE_BACKUP_MODULE_ID,
    normalizedCollection,
    {
      label: PRINT_TEMPLATE_BACKUP_LABEL,
      ...options,
    }
  );
}

function mergePrintTemplateCollections(baseCollection, incomingCollection) {
  const merged = normalizePrintTemplateCollection({
    templates: [
      ...(baseCollection?.templates || []),
      ...(incomingCollection?.templates || []),
    ],
  });

  const dedupedByScopeVersion = new Map();
  merged.templates.forEach((template) => {
    const key = [
      template.restaurant_id || '',
      template.template_key,
      template.channel,
      template.template_format,
      template.version,
    ].join(':');
    dedupedByScopeVersion.set(key, template);
  });

  return normalizePrintTemplateCollection({
    templates: [...dedupedByScopeVersion.values()],
  });
}

export async function fetchRestaurantPrintTemplatesOnline(options = {}) {
  const partnerToken = readPartnerToken();
  if (!partnerToken) {
    throw new Error('No hay token de partner activo para descargar plantillas de impresion.');
  }

  const response = await axios.get(`${getApiBaseUrl()}/v1/api/partner/print-templates`, {
    headers: { Authorization: `Bearer ${partnerToken}` },
    timeout: options.timeoutMs || PRINT_TEMPLATE_REQUEST_TIMEOUT_MS,
  });

  const collection = normalizePrintTemplateCollection(response.data || []);
  const restaurantId = normalizeId(
    options.restaurantId
    || collection.templates[0]?.restaurant_id
  );

  if (restaurantId) {
    await saveCachedRestaurantPrintTemplates(restaurantId, collection.templates, {
      saved_at: options.saved_at,
    });
  }

  markBackendAvailable();
  return collection;
}

export async function fetchRuntimePrintTemplateOnline(templateKey, options = {}) {
  const headers = buildAuthHeaders();
  if (!headers.Authorization) {
    throw new Error('No hay token activo para descargar la plantilla de impresion.');
  }

  const response = await axios.get(`${getApiBaseUrl()}/v1/api/app/print-template`, {
    headers,
    timeout: options.timeoutMs || PRINT_TEMPLATE_REQUEST_TIMEOUT_MS,
    params: {
      template_key: templateKey,
      channel: options.channel || PRINT_TEMPLATE_DEFAULT_CHANNEL,
      template_format: options.templateFormat || PRINT_TEMPLATE_DEFAULT_FORMAT,
    },
  });

  const template = normalizePrintTemplate(response.data || {});
  const restaurantId = normalizeId(
    options.restaurantId
    || template.restaurant_id
  );

  if (restaurantId && template.template_key) {
    const cachedCollection = await getCachedRestaurantPrintTemplates(restaurantId);
    const mergedCollection = mergePrintTemplateCollections(cachedCollection, { templates: [template] });
    await saveCachedRestaurantPrintTemplates(restaurantId, mergedCollection.templates, {
      saved_at: options.saved_at,
    });
  }

  if (restaurantId) {
    rememberRestaurantId(restaurantId);
  }
  markBackendAvailable();
  return template;
}

export async function warmRestaurantPrintTemplatesCache(options = {}) {
  try {
    return await fetchRestaurantPrintTemplatesOnline(options);
  } catch (error) {
    if (isBackendUnavailableError(error)) {
      markBackendUnavailable(error?.message || 'No se pudieron refrescar las plantillas de impresion.');
    }
    throw error;
  }
}

export async function resolvePrintTemplate(templateKey, options = {}) {
  const normalizedTemplateKey = normalizeText(templateKey);
  const normalizedChannel = normalizeText(options.channel, PRINT_TEMPLATE_DEFAULT_CHANNEL);
  const normalizedFormat = normalizeText(options.templateFormat, PRINT_TEMPLATE_DEFAULT_FORMAT);
  let restaurantId = await resolveRestaurantId(options);

  if (!shouldUseOfflineMode()) {
    try {
      const partnerToken = readPartnerToken();
      if (partnerToken && partnerToken !== partnerTemplatesDeniedToken) {
        let onlineCollection = null;
        try {
          onlineCollection = await fetchRestaurantPrintTemplatesOnline({
            restaurantId,
            timeoutMs: options.timeoutMs,
          });
        } catch (error) {
          // Tras el cambio rápido de usuario, el token de gestor o camarero ocupa el del
          // partner, pero la lista de plantillas es solo del partner: usa la publicada.
          if (![401, 403].includes(error?.response?.status)) throw error;
          partnerTemplatesDeniedToken = partnerToken;
        }
        const onlineTemplate = onlineCollection && selectPublishedPrintTemplate(onlineCollection.templates, {
          templateKey: normalizedTemplateKey,
          channel: normalizedChannel,
          templateFormat: normalizedFormat,
        });
        if (onlineTemplate) {
          return {
            template: onlineTemplate,
            source: 'online',
            restaurant_id: normalizeId(onlineTemplate.restaurant_id) || restaurantId,
          };
        }
      }
      if (!partnerToken || partnerToken === partnerTemplatesDeniedToken) {
        const runtimeTemplate = await fetchRuntimePrintTemplateOnline(normalizedTemplateKey, {
          restaurantId,
          channel: normalizedChannel,
          templateFormat: normalizedFormat,
          timeoutMs: options.timeoutMs,
        });
        if (runtimeTemplate?.template_key) {
          return {
            template: runtimeTemplate,
            source: 'online',
            restaurant_id: normalizeId(runtimeTemplate.restaurant_id) || restaurantId,
          };
        }
      }
    } catch (error) {
      if (isBackendUnavailableError(error)) {
        markBackendUnavailable(error?.message || 'No se pudo consultar la plantilla de impresion online.');
      }
      if (options.strictOnline) {
        throw error;
      }
    }
  }

  restaurantId = restaurantId || readRememberedRestaurantId();
  if (restaurantId) {
    const cachedCollection = await getCachedRestaurantPrintTemplates(restaurantId);
    const cachedTemplate = selectPublishedPrintTemplate(cachedCollection.templates, {
      templateKey: normalizedTemplateKey,
      channel: normalizedChannel,
      templateFormat: normalizedFormat,
    });
    if (cachedTemplate) {
      return {
        template: cachedTemplate,
        source: 'backup',
        restaurant_id: restaurantId,
      };
    }
  }

  return {
    template: null,
    source: 'missing',
    restaurant_id: restaurantId || null,
  };
}

export function buildPrintTemplateHtmlDocument(template, bodyHtml, options = {}) {
  const normalizedTemplate = normalizePrintTemplate(template);
  const title = normalizeText(options.title || normalizedTemplate.label || normalizedTemplate.template_key || 'print-template', 'print-template');

  return [
    '<!doctype html>',
    '<html lang="es">',
    '<head>',
    '  <meta charset="utf-8" />',
    '  <meta name="viewport" content="width=device-width, initial-scale=1" />',
    `  <title>${title}</title>`,
    `  <style>${normalizedTemplate.css_template || ''}</style>`,
    '</head>',
    '<body>',
    String(bodyHtml || ''),
    '</body>',
    '</html>',
  ].join('\n');
}
